import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { isListingFavorite } from '../../ducks/favorites.duck';

import { IconHeart } from '../../components';

import css from './FavoriteButton.module.css';

/**
 * Heart button toggling a listing as favorite. Optional wherever it's used:
 * the caller decides whether to render it at all (e.g. hidden on the user's
 * own listings).
 *
 * A guest (no currentUser) is redirected to the login page on click, and
 * brought back to the page they were on afterwards (same `from` idiom as
 * AuthenticationPage / Routes.js).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {propTypes.uuid} props.listingId - the listing this button toggles
 * @param {propTypes.currentUser?} props.currentUser - the current user, if any
 * @param {Function} props.onToggleFavorite - (listingId) => Promise, dispatches the toggle
 * @returns {JSX.Element}
 */
const FavoriteButton = props => {
  const { rootClassName, className, listingId, currentUser, onToggleFavorite } = props;
  const [pending, setPending] = useState(false);
  const [hasError, setHasError] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  const isFavorite = isListingFavorite(currentUser, listingId);
  const classes = classNames(rootClassName || css.root, className);

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser?.id) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
      return;
    }

    if (pending) {
      return;
    }

    setPending(true);
    setHasError(false);
    onToggleFavorite(listingId)
      .then(() => {
        setPending(false);
      })
      .catch(() => {
        setPending(false);
        setHasError(true);
      });
  };

  const label = intl.formatMessage({
    id: isFavorite ? 'FavoriteButton.remove' : 'FavoriteButton.add',
  });

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      disabled={pending}
      aria-label={label}
      aria-pressed={isFavorite}
    >
      <IconHeart className={css.icon} isFilled={isFavorite} />
      {hasError ? (
        <span className={css.error}>
          <FormattedMessage id="FavoriteButton.error" />
        </span>
      ) : null}
    </button>
  );
};

export default FavoriteButton;
